import * as Yup from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { BeatLoader } from "react-spinners";
import { useEffect, useState } from "react";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate, useParams } from "react-router-dom";
import { resetPassword } from "../redux/ApiSlice/ForgotPasswordSlice";
import { buildIdentifierFields } from "../utils/identifier";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { IoIosCheckmarkCircle } from "react-icons/io";

const ChangePassword = () => {
    const params = useParams();
    const { otp, email } = params
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumberOrSpecialChar: false,
    });
    const [termsAccepted, setTermsAccepted] = useState(false);

    const navigate = useNavigate();

    const handleSubmitPassword = async (values) => {
        setLoading(true);
        try {
            const data = {
                password: values.password,
                cpassword: values.confirmPassword,
                otp: otp,
                ...buildIdentifierFields(email),
            }
            const response = await dispatch(resetPassword(data)).unwrap();

            if (response.message) {
                formik.resetForm();
                navigate("/password-updated", { state: { fromReset: true } });
            } else {
                toast.error("Password reset failed.");
                setLoading(false);
            }
        } catch (error) {
            console.error("Error:", error);
            if (error?.password) {
                toast.error(error?.password[0]);
            } else if (error?.otp) {
                toast.error(error?.otp[0]);
            } else {
                toast.error(error?.error?.[0] ?? "Something went wrong.");
            }
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    const validateSchema = Yup.object({
        password: Yup.string()
            .required("Please Enter your password")
            .matches(
                /^(?=.*[a-z])(?=.*[A-Z])(?:(?=.*\d)|(?=.*[^A-Za-z0-9])).{8,}$/,
                "Must contain at least one uppercase letter, one lowercase letter, and either a number or a special character"
            ),

        confirmPassword: Yup.string()
            .required("Please confirm your password")
            .oneOf([Yup.ref("password"), null], "Passwords must match"),
    });

    const formik = useFormik({
        initialValues: {
            password: "",
            confirmPassword: "",
        },
        validationSchema: validateSchema,

        onSubmit: (values, { resetForm }) => {
            handleSubmitPassword(values);
        },
    });

    useEffect(() => {
        const value = formik.values.password || "";
        setPasswordValidation({
            minLength: value.length >= 8,
            hasUpperCase: /[A-Z]/.test(value),
            hasLowerCase: /[a-z]/.test(value),
            hasNumberOrSpecialChar: /[0-9!@#$%^&*(),.?":{}|<>]/.test(value),
        });
    }, [formik.values.password]);

    const meetsChecklist = Object.values(passwordValidation).every(Boolean);
    const passwordsFilled = Boolean(formik.values.password) && Boolean(formik.values.confirmPassword);
    const passwordsMatch = formik.values.password === formik.values.confirmPassword;
    const hasErrors = Object.keys(formik.errors).length > 0;
    const disableReset = loading || !passwordsFilled || !meetsChecklist || !passwordsMatch || hasErrors || !termsAccepted;
    const passwordError = formik.touched.password && formik.errors.password;
    const confirmError = formik.touched.confirmPassword && formik.errors.confirmPassword;

    return (
        <div className="h-full w-full bg-white dark:bg-[#1E1D21]">
            <div className="sticky top-0">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2 z-10">
                    <IoMdArrowBack
                        onClick={() => navigate(-1)}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black flex-grow">Forgot password</p>
                </div>
            </div>

            <div className="w-full h-full p-4">
                <p className="text-[18px] font-semibold text-black dark:text-white">
                    Create a new password
                </p>
                <p className="text-[11px] mt-1 font-semibold text-gray-500">
                    Your new password must be different from previous used passwords.
                </p>

                <form className="w-full h-full mt-5" onSubmit={formik.handleSubmit}>
                    <div className={`px-4 pt-4 pb-3 rounded-xl border-2 mt-5 ${passwordError ? "border-red-400" : "border-[#BDE6FF]"}`}>
                        <p className="text-[12px] font-semibold text-black dark:text-white">New Password</p>
                        <div className="relative">
                            <input
                                name="password"
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                value={formik.values.password}
                                placeholder="Enter New Password"
                                type={showPassword ? "text" : "password"}
                                className="w-full text-[12px] focus:outline-none text-black dark:text-white bg-white dark:bg-[#1E1D21]"
                            />
                            <div className="absolute right-0 top-1 cursor-pointer">
                                {showPassword ? (
                                    <AiOutlineEyeInvisible
                                        className="text-[20px] text-black dark:text-white"
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                ) : (
                                    <AiOutlineEye
                                        className="text-[20px] text-black dark:text-white"
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {formik.touched.password && formik.errors.password && (
                        <p className="mt-1 ml-2 text-[12px] text-[#ff3b3b] font-semibold">
                            {formik.errors.password}
                        </p>
                    )}

                    <div className={`px-4 pt-4 pb-3 rounded-xl border-2 mt-3 ${confirmError ? "border-red-400" : "border-[#BDE6FF]"}`}>
                        <p className="text-[12px] font-semibold text-black dark:text-white">Confirm New Password</p>
                        <div className="relative">
                            <input
                                name="confirmPassword"
                                onBlur={formik.handleBlur}
                                placeholder="Confirm Password"
                                value={formik.values.confirmPassword}
                                onChange={(e) => { formik.handleChange(e); }}
                                type={showConfirmPassword ? "text" : "password"}
                                className="w-full text-[12px] focus:outline-none text-black dark:text-white bg-white dark:bg-[#1E1D21]"
                            />
                            <div className="absolute right-0 top-1 cursor-pointer">
                                {showConfirmPassword ? (
                                    <AiOutlineEyeInvisible
                                        className="text-[20px] text-black dark:text-white"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    />
                                ) : (
                                    <AiOutlineEye
                                        className="text-[20px] text-black dark:text-white"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                        <p className="mt-1 ml-2 text-[12px] text-[#ff3b3b] font-semibold">
                            {formik.errors.confirmPassword}
                        </p>
                    )}
                    <div className="mt-3 ml-3.5">
                        <div className="space-y-1">
                            <div className="flex items-center gap-x-1">
                                <IoIosCheckmarkCircle className={`h-4 w-4 rounded-full ${passwordValidation.minLength ? "text-cyan-500" : "text-gray-300"}`} />
                                <p className={`text-[11px] ${passwordValidation.minLength ? "text-cyan-500 font-semibold" : "text-gray-600 dark:text-gray-300"}`}>
                                    At least 8 characters
                                </p>
                            </div>
                            <div className="flex items-center gap-x-1">
                                <IoIosCheckmarkCircle className={`h-4 w-4 rounded-full ${passwordValidation.hasUpperCase ? "text-cyan-500" : "text-gray-300"}`} />
                                <p className={`text-[11px] ${passwordValidation.hasUpperCase ? "text-cyan-500 font-semibold" : "text-gray-600 dark:text-gray-300"}`}>
                                    One uppercase letter
                                </p>
                            </div>
                            <div className="flex items-center gap-x-1">
                                <IoIosCheckmarkCircle className={`h-4 w-4 rounded-full ${passwordValidation.hasLowerCase ? "text-cyan-500" : "text-gray-300"}`} />
                                <p className={`text-[11px] ${passwordValidation.hasLowerCase ? "text-cyan-500 font-semibold" : "text-gray-600 dark:text-gray-300"}`}>
                                    One lowercase letter
                                </p>
                            </div>
                            <div className="flex items-center gap-x-1">
                                <IoIosCheckmarkCircle className={`h-4 w-4 rounded-full ${passwordValidation.hasNumberOrSpecialChar ? "text-cyan-500" : "text-gray-300"}`} />
                                <p className={`text-[11px] ${passwordValidation.hasNumberOrSpecialChar ? "text-cyan-500 font-semibold" : "text-gray-600 dark:text-gray-300"}`}>
                                    One number or special character
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* <div className="flex items-center ml-5 mt-3 mb-4 md:mb-2">
                        <p className="flex gap-2 items-center text-[12px] text-gray-700">
                            <Checkbox
                                color="blue"
                                id="vertical-list-react"
                                ripple={false}
                                className="hover:before:opacity-0 h-4 w-4"
                                containerProps={{
                                    className: "p-[1px] border-gray-300",
                                }}
                            />
                            Logout from all devices
                        </p>
                    </div> */}
                    <div className="mt-3 ml-4 flex items-center gap-x-2">
                        <input
                            type="checkbox"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            className="h-3 w-3 rounded cursor-pointer accent-gray-800 hover:accent-gray-900"
                        />
                        <p className="text-[11px] text-gray-600 dark:text-white text-left font-semibold">
                            I agree to the <span className="text-cyan-500 font-bold cursor-pointer" onClick={() => navigate("/terms-of-use")}>Terms of Use</span> and
                            our <span className="text-cyan-500 font-bold cursor-pointer" onClick={() => navigate("/privacy-policy")}>Privacy Policy</span>
                        </p>
                    </div>
                    <button
                        type="submit"
                        disabled={disableReset}
                        className="h-12 text-[16px] font-semibold px-3 bg-[#BDE6FF] text-black dark:text-white rounded-xl w-full my-6 disabled:bg-gray-300 disabled:dark:bg-[#333333] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {loading ? <BeatLoader
                            size={10}
                            color={"#fff"}
                            loading={loading}
                        />
                            : "Reset password"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ChangePassword;